import React, { useEffect, useRef, useState } from "react";
import styles from "./AddTaskPanel.module.css";
import CrossButton from "../Customs/Button/CrossButton"; 

import DocumentUploadPopup from "./DocumentUploadPopup";
import WebService from "../Services/WebService";

// Employee interface
interface Employee {
  user_id: number;
  username: string;
  mobile_number?: string;
}

interface AddTaskPanelProps {
  open: boolean;
  onClose: () => void;
  canWrite?: boolean; 
}

const AddTaskPanel: React.FC<AddTaskPanelProps> = ({ open, onClose }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Form states
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [priority, setPriority] = useState("High");
  const [status, setStatus] = useState("PENDING");
  const [assignee, setAssignee] = useState<Employee | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [searchEmp, setSearchEmp] = useState("");
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
 const [endTouched, setEndTouched] = useState(false);
  const [weightage, setWeightage] = useState(1);
  // const [tags, setTags] = useState(""); 
  const [, setNextClicked] = useState(false);
 const [submitting, setSubmitting] = useState(false); // instead of nextClicked
  const [errors, setErrors] = useState<any>({});
  const [showDocPopup, setShowDocPopup] = useState(false);
  const [createdTaskId, setCreatedTaskId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    WebService.getAPI({ action: "/v1/door-management/employees?limit=100&page_number=1" })
      .then((result: any) => {
        if (
          result &&
          result.status_code === 200 &&
          result.data &&
          Array.isArray(result.data.employees)
        ) {
          setEmployees(result.data.employees);
        } else {
          setEmployees([]);
          setNextClicked(false);
        }
      })
      .catch(() => setEmployees([]));
  }, [open]);
  

  const validate = () => {
    const errs: any = {};
    if (!subject.trim()) errs.subject = "Task Name is required";
    if (!description.trim()) errs.description = "Description is required";
    if (!startDate) errs.start_date = "Start date is required";
    if (!endDate) errs.end_date = "End date is required";
    if (!assignee) errs.assign_to = "Select an employee";
    if (weightage <= 0) errs.weightage = "Weightage must be positive";
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start > end) {
        errs.start_date = "Start date cannot be after end date";
        errs.end_date = "End date must be after or same as start date";
      }
    }
    return errs;
  };

const addEmployee = (emp: Employee) => {
  setAssignee(emp);
  setSearchEmp(emp.username || emp.mobile_number || `User #${emp.user_id}`);
  setShowEmpDropdown(false);
  setErrors((prev: any) => {
    const next = { ...prev };
    delete next.assign_to;
    return next;
  });
};
const removeEmployee = () => {
  setAssignee(null);
  setSearchEmp("");        
  setShowEmpDropdown(false);
};


  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    if (open) {
      setSubject("");
      setDescription("");
      setStartDate("");
      setEndDate("");
      setPriority("High");
      setStatus("PENDING");
      setAssignee(null);
      setErrors({});
      setShowDocPopup(false);
      setSearchEmp("");
      setWeightage(1);
      setCreatedTaskId(null);
      setEndTouched(false);
      // setTags("");
    }
  }, [open]);

const filteredEmployees = employees.filter((emp) => {
  const q = searchEmp.trim().toLowerCase();
  if (!q) return true;
  const name = (emp.username || "").toLowerCase();
  const mobile = (emp.mobile_number || "").toLowerCase();
  return name.includes(q) || mobile.includes(q);
});

const employeeLabel = (emp: Employee) =>
  emp.username?.trim() || emp.mobile_number || `User #${emp.user_id}`;



const handleNext = async (e: React.FormEvent) => {
  e.preventDefault();
  if (submitting) return; // prevent double submit while in-flight

  // Validate first (button still enabled)
  const errs = validate();
  setErrors(errs);
  if (Object.keys(errs).length > 0) {
    return; // do not lock; user should be able to fix and re-click
  }

  setSubmitting(true); 

  const payload = {
    subject,
    description,  
  start_date: `${startDate}T00:00:00Z`,
  end_date: `${endDate}T23:59:59Z`,
    assign_to: assignee?.user_id,
    status: status.toUpperCase(),
    priority: priority.toUpperCase(),
    weightage,
  };

  try {
    const result: any = await WebService.postAPI({
      action: "/v1/door-management/tasks",
      body: payload,
    });

    // Be tolerant to shapes: {data:{id}}, {data:{task_id}}, {id}
    const ok = result && (result.status_code === 201 || result.status_code === 200);
    const newId =
      result?.data?.id ??
      result?.data?.task_id ??
      result?.id ??
      null;

    if (ok && newId) {
      setCreatedTaskId(newId);
      setShowDocPopup(true); // keep button disabled while popup is open (expected)
    } else {
      setErrors({ form: result?.message || "Failed to create task." });
      setSubmitting(false); // unlock for retry
    }
  } catch (err: any) {
    setErrors({ form: typeof err === "string" ? err : (err?.message || "Error creating task. Try again.") });
    setSubmitting(false); // unlock for retry
  }
};



  return (
    <>
      <div
        className={`${styles.overlay} ${open ? styles.open : ""}`}
        onClick={handleOverlayClick}
        tabIndex={-1}
        aria-modal="true"
      >
        <div
          className={`${styles.panel} ${open ? styles.slideIn : ""}`}
          ref={panelRef}
          onClick={e => e.stopPropagation()}
        >
          <form className={styles.form} autoComplete="off" onSubmit={handleNext}>
            <div className={styles.headerRow}>
              <div>
                <span className={styles.title}>Create Task</span>
                <div className={styles.subtitle}>
                  Create a new task by filling in the details below
                </div>
              </div>
<CrossButton onClick={onClose} className={styles.closeBtn} />

            </div>
            <div className={styles.grid}>
              <div className={styles.leftCol}>
                <div className={styles.inputGroup}>
                  <label className={styles.label}>Task Name</label>
                  <input
                    className={styles.input}
                    placeholder="Task Name"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    style={errors.subject ? { borderColor: "#e43e2d" } : {}}
                  />
                  {errors.subject && <div style={{ color: "#e43e2d", fontSize: 13 }}>{errors.subject}</div>}
                </div>
                <div className={styles.inputGroup}>
                  <label className={styles.label}>Description</label>
                  <textarea
                    className={styles.input}
                    placeholder="Task Description"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    style={errors.description ? { borderColor: "#e43e2d" } : {}}
                  />
                  {errors.description && <div style={{ color: "#e43e2d", fontSize: 13 }}>{errors.description}</div>}
                </div>
{/* Start Date */}
<div className={styles.inputGroup}>
  <label className={styles.label}>Start Date</label>
  <div className={styles.inputWithIcon}>
    <input
      className={styles.input}
      type="date"
      value={startDate}
      onChange={(e) => {
        const newStart = e.target.value;
        setStartDate(newStart);

        // if user hasn't manually touched endDate OR endDate is empty/invalid
        if (
          !endTouched ||
          !endDate ||
          new Date(endDate) <= new Date(newStart)
        ) {
          const d = new Date(newStart);
          d.setDate(d.getDate() + 1);
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          setEndDate(`${yyyy}-${mm}-${dd}`);
        }
      }}
      style={errors.start_date ? { borderColor: "#e43e2d" } : {}}
    />
  </div>
  {errors.start_date && (
    <div style={{ color: "#e43e2d", fontSize: 13 }}>
      {errors.start_date}
    </div>
  )}
</div>

{/* End Date */}
<div className={styles.inputGroup}>
  <label className={styles.label}>End Date</label>
  <div className={styles.inputWithIcon}>
    <input
      className={styles.input}
      type="date"
      value={endDate}
      min={startDate || undefined}
      onChange={(e) => {
        setEndTouched(true);
        setEndDate(e.target.value);
      }}
      style={errors.end_date ? { borderColor: "#e43e2d" } : {}}
    />
  </div>
  {errors.end_date && (
    <div style={{ color: "#e43e2d", fontSize: 13 }}>
      {errors.end_date}
    </div>
  )}
</div>

                {/* <div className={styles.inputGroup}>
                  <label className={styles.label}>Tags</label>
                  <input
                    className={styles.input + " " + styles.tagsInput}
                    placeholder="Enter tags comma separated (e.g. SO-1,SO-2)"
                    value={tags}
                    onChange={e => setTags(e.target.value)}
                  />
                  <div className={styles.tagsDisplay}>
                    {tags.split(",").filter(Boolean).map(tag =>
                      <span className={styles.tagChip} key={tag.trim()}>{tag.trim()}</span>
                    )}
                  </div>
                </div> */}
                <div className={styles.inputGroup}>
                  <label className={styles.label}>Weightage</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                    <button
                      type="button"
                      className={styles.weightageBtn}
                      aria-label="Decrease weightage"
                      onClick={() => setWeightage(Math.max(1, weightage - 1))}
                    >-</button>
                    <div className={styles.weightageBadge}>
                      {weightage}
                    </div>
                    <button
                      type="button"
                      className={styles.weightageBtn}
                      aria-label="Increase weightage"
                      onClick={() => setWeightage(weightage + 1)}
                    >+</button>
                  </div>
                  {errors.weightage && <div style={{ color: "#e43e2d", fontSize: 13 }}>{errors.weightage}</div>}
                </div>
              </div>
              <div className={styles.rightCol}>
<div className={styles.inputGroup}>
  <label className={styles.label}>Assign Employee</label>
  <div className={styles.employeeField}>
    {assignee && (
      <span className={styles.employeeChip}>
        {employeeLabel(assignee)}
        <button
          type="button"
          className={styles.removeEmployeeBtn}
          onMouseDown={(e) => e.preventDefault()}
          onClick={removeEmployee}
        >
          &times;
        </button>
      </span>
    )}

    <input
      className={styles.searchInput}
      placeholder={assignee ? "Change assignee…" : "Search member…"}
      value={searchEmp}
      onChange={(e) => {
        const value = e.target.value;
        setSearchEmp(value);
        if (assignee) setAssignee(null);
        setShowEmpDropdown(true);
      }}
      onFocus={() => setShowEmpDropdown(true)}
      onBlur={() => setTimeout(() => setShowEmpDropdown(false), 200)}
      autoComplete="off"
    />

    {showEmpDropdown && !assignee && (
      <div
        className={styles.empDropdown}
        onMouseDown={(e) => e.preventDefault()}
      >
        {filteredEmployees.length > 0 ? (
          filteredEmployees.map((emp) => (
            <div
              key={emp.user_id}
              className={styles.empDropdownItem}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addEmployee(emp);
              }}
            >
              {employeeLabel(emp)}
            </div>
          ))
        ) : (
          <div className={styles.empDropdownItem} style={{ cursor: "default", color: "#888" }}>
            No members found
          </div>
        )}
      </div>
    )}
  </div>
  {errors.assign_to && <div style={{ color: "#e43e2d", fontSize: 13 }}>{errors.assign_to}</div>}
</div>

                <div className={styles.inputGroup}>
                  <label className={styles.label}>Priority Level</label>
                  <div className={styles.priorityRow}>
                    {["High", "Medium", "Low"].map(lvl => (
                      <button
                        key={lvl}
                        type="button"
                        className={`${styles.priorityBtn} ${priority === lvl ? styles[lvl.toLowerCase()] : ""}`}
                        onClick={() => setPriority(lvl)}
                        style={priority === lvl ? { borderWidth: 2 } : {}}
                      >
                        {lvl}
                      </button>
                    ))}
                  </div>
                </div>
                <div className={styles.inputGroup}>
                  <label className={styles.label}>Status</label>
                  <select
                    className={styles.input}
                    value={status}
                    onChange={e => setStatus(e.target.value)}
                  >
                    <option value="PENDING">Pending</option>
                    <option value="PROCESSING">Processing</option>
                    <option value="PAUSED">Paused</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                </div>
              </div>
            </div>
            <div className={styles.footerBtns}>
           <button type="submit" className={styles.createBtn} disabled={submitting || showDocPopup}>
          {submitting ? "Creating…" : "Next"}
        </button>

            </div>
            {errors.form && <div style={{ color: "#e43e2d", marginTop: 10 }}>{errors.form}</div>}
          </form>
        </div>
      </div>
    <DocumentUploadPopup
       open={showDocPopup}
       onClose={() => {
       setShowDocPopup(false);
       onClose();      
       }}
       taskId={createdTaskId}
       onSuccess={() => {
       setShowDocPopup(false);
       onClose();
        }}
       />
        </>
  );
};
export default AddTaskPanel;
